'use strict';

import { unwrap, prepareWrapper, TraverseValue } from './internal';

const DEFAULTS = {
  max:     1000,
  maxArgs: 32,
  maxSize: 1_000_000,
};

// canonize traverse order aliases
const ORDER: Record<string, 'LI' | 'LO'> = {
  'leftmost-outermost': 'LO',
  'leftmost-innermost': 'LI',
  LO:                   'LO',
  LI:                   'LI',
};

/**
 *  Control primitives for fold() and traverse() methods.
 */
export const control = {
  descend: prepareWrapper('descend'),
  prune:   prepareWrapper('prune'),
  redo:    prepareWrapper('redo'),
  stop:    prepareWrapper('stop'),
};

export type InferOptions = {
  max?: number,
  maxArgs?: number,
  maxSize?: number,
};

export type TermInfo = {
  normal: boolean,      // whether the term becomes irreducible after receiving a number of arguments.
  proper: boolean,      // whether the irreducible form is only contains its arguments. implies normal.
  arity?: number,       // the number of arguments that is enough to reach the irreducible form
  discard?: boolean,    // whether the term (or subterms, unless proper) can discard arguments.
  duplicate?: boolean,  // whether the term (or subterms, unless proper) can duplicate arguments.
  skip?: Set<number>,   // indices of arguments that are discarded. nonempty implies discard.
  dup?: Set<number>,    // indices of arguments that are duplicated. nonempty implies duplicate.
  expr?: Expr,          // canonical form containing only lambdas, applications, and variables, if one exists
  steps?: number,       // number of reduction  steps taken to obtain the aforementioned information, if applicable
}

export type Invocation = Expr | ((arg: Expr) => Invocation);
export type Step = { expr: Expr, steps: number, changed: boolean };
export type Run = { expr: Expr, steps: number, final: boolean };
export type RunOptions = { max?: number, steps?: number, throw?: boolean, maxSize?: number };

export type FormatOptions = {
  terse?:       boolean,
  html?:        boolean,
  brackets?:    [string, string],
  space?:       string,
  var?:         [string, string],
  lambda?:      [string, string, string],
  around?:      [string, string],
  redex?:       [string, string],
  declaration?: [string, string, string],
  inventory?:   Record<string, Named>,
};

/**
 *  A version of FormatOptions with defaults plugged in,
 *       use for mandatory formatImpl implementation in Expr subclasses.
 */
export type RefinedFormatOptions = { // ditto but with defaults plugged in
  terse?: boolean,
  html?: boolean,
  brackets: [string, string],
  space: string,
  var: [string, string],
  lambda: [string, string, string],
  around: [string, string],
  redex: [string, string],
  inventory?: Record<string, Expr>,
}

export type TraverseOptions = {order?: 'LO' | 'LI' | 'leftmost-outermost' | 'leftmost-innermost'};
export type TraverseCallback = (e:Expr) => TraverseValue<Expr>;

export type ToposortResult = { list: Expr[], env: Record<string, Named> };

/**
 *   A combinatory logic expression.
 *
 *  Applications, variables, lambdas, combinators per se,
 *  and other expression subtypes all extend this class.
 *
 *  Expr itself cannot (or at least should not) be instantiated.
 *
 *  @abstract
 */
export abstract class Expr {
  /**  optional context for the term. Is set by the parser with addContext: true */
  context?: {
    scope?: object,
    env?: Record<string, Expr>,
    src?: string,
    parser: object,
  }

  /**  number of arguments the term is waiting for (if known) */
  arity?: number;
  /**  a brief description what the term does */
  note?: string;
  /**  the properties of the term, typically inferred from its behavior.
   *      This is used internally when declaring Primitive / Alias terms.
   */
  props?: TermInfo;
  /**  An estimated number of nodes in the expression tree.
   *     Used to prevent runaway computations.
   */
  size: number = 1;

  /**
   * Add metadata based on user-supplied values and/or the properties of the term itself.
   *
   * Typically applied to named terms shortly after instantiation.
   *
   * Experimental. Name and meaning may change in the future.
   *
   * @experimental
   * @param {Object} options
   * @param {string} [options.note] - a brief description what the term does
   * @param {number} [options.arity] - number of arguments the term is waiting for (if known)
   * @param {string} [options.fancy] - how to display in html mode, e.g. &phi; instead of 'f'
   * @param {boolean} [options.canonize] - whether to try to infer the properties
   * @param {number} [options.max] - maximum number of steps for inference, if canonize is true
   * @param {number} [options.maxArgs] - maximum number of arguments for inference, if canonize is true
   * @return {this}
   */
  annotate (options: { note?: string, arity?: number, fancy?: string, canonize?: boolean, max?: number, maxArgs?: number, } = {}) {
    // TODO better name

    if (options.fancy !== undefined && this instanceof Named)
      this.fancyName = options.fancy;

    if (options.note !== undefined)
      this.note = options.note;

    if (options.arity !== undefined)
      this.arity = options.arity;

    if (options.canonize) {
      const guess = this.infer(options);
      if (guess.normal) {
        this.arity = this.arity ?? guess.arity;
        this.note = this.note ?? guess.expr!.format({ html: true, lambda: ['', ' &mapsto; ', ''] });
        delete guess.steps;
        this.props = guess;
      }
    }
    return this;
  }

  /**
   *  apply self to zero or more terms and return the resulting term,
   * without performing any calculations whatsoever
   * @param {Expr} args
   * @return {Expr}
   */
  apply (...args: Expr[]): Expr {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let expr: Expr = this;
    for (const arg of args)
      expr = new App(expr, arg);
    return expr;
  }

  /**
   *  Replace all aliases in the expression with their definitions, recursively.
   * @return {Expr}
   */
  expand (): Expr {
    return this.traverse(e => {
      if (e instanceof Alias)
        return e.impl.expand();
    }) ?? this;
  }

  /**
   *  Traverse the expression tree, applying change() to each node.
   *       If change() returns an Expr, the node is replaced with that value.
   *       A null/undefined value is interpreted as
   *       "descend further if applicable, or leave the node unchanged".
   *
   *       Returned values may be decorated:
   *
   *       SKI.control.prune will suppress further descending even if nothing was returned
   *       SKI.control.stop will terminate further changes.
   *       SKI.control.redo will apply the callback to the returned subtree, recursively.
   *
   *       Note that if redo was applied at least once to a subtree, a null return from the same subtree
   *       will be replaced by the last non-null value returned.
   *
   *       The traversal order is leftmost-outermost, unless options.order = 'leftmost-innermost' is specified.
   *       Short aliases 'LO' and 'LI' (case-sensitive) are also accepted.
   *
   *       Returns null if no changes were made, or the new expression otherwise.
   *
   * @param {{
   *   order?: 'LO' | 'LI' | 'leftmost-outermost' | 'leftmost-innermost',
   * }} [options]
   * @param {(e:Expr) => TraverseValue<Expr>} change
   * @returns {Expr|null}
   * @sealed
   */
  traverse (
    options: TraverseOptions | TraverseCallback,
    change?: TraverseCallback
  ): Expr | null {
    if (typeof options === 'function') {
      change = options;
      options = {};
    }
    const order = ORDER[options.order ?? 'LO'];
    if (order === undefined)
      throw new Error('Unknown traversal order: ' + options.order);
    const [expr] = unwrap(this._traverse_redo({ order }, change!));
    return expr ?? null;
  }

  /**
   *
   *
   * @protected
   *
   * @param {Object} options
   * @param {(e:Expr) => TraverseValue<Expr>} change
   * @returns {TraverseValue<Expr>}
   */
  _traverse_redo (options: TraverseOptions, change: TraverseCallback): TraverseValue<Expr> {
    let action;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let expr: Expr | undefined = this;
    let prev;
    do {
      prev = expr;
      const next: TraverseValue<Expr> = options.order === 'LI'
        ? expr._traverse_descend(options, change) ?? change(expr)
        : change(expr) ?? expr._traverse_descend(options, change);
      [expr, action] = unwrap(next);
    } while (expr && action === control.redo);
    if (!expr && prev !== this)
      expr = prev; // we were in redo at least once
    return action ? action(expr!) : expr;
  }

  /**
   * Apply a {@link traverse} callback to the subterms of the expression, without changing the expression itself.
   *
   * @protected
   * @param {Object} options
   * @param {(e:Expr) => TraverseValue<Expr>} change
   * @returns {TraverseValue<Expr>}
   */
  _traverse_descend (options: TraverseOptions, change: TraverseCallback): TraverseValue<Expr> {
    return null;
  }

  /**
   *  Returns true if predicate() is true for any subterm of the expression, false otherwise.
   *
   * @param {(e: Expr) => boolean} predicate
   * @returns {boolean}
   */
  any (predicate: (e: Expr) => boolean): boolean {
    return predicate(this);
  }

  /**
   *  Fold the expression into a single value by recursively applying combine() to its subterms.
   *       Nodes are traversed in leftmost-outermost order, i.e. the same order as reduction steps are taken.
   *
   * null or undefined return value from combine() means "keep current value and descend further".
   *
   * SKI.control provides primitives to control the folding flow:
   *  - SKI.control.prune(value) means "use value and don't descend further into this branch";
   *  - SKI.control.stop(value) means "stop folding immediately and return value".
   *  - SKI.control.descend(value) is the default behavior, meaning "use value and descend further".
   *
   * This method is experimental and may change in the future.
   *
   * @example // count the number of nodes in the expression tree
   * expr.fold(0, (acc, e) => acc + 1);
   *
   * @experimental
   *
   * @template T
   * @param {T} initial
   * @param {(acc: T, expr: Expr) => TraverseValue<T>} combine
   * @returns {T}
   */
  fold<T> (initial: T, combine: (acc: T, expr: Expr) => TraverseValue<T>): T {
    const [value] = unwrap(this._fold(initial, combine));
    return value ?? initial;
  }

  /**
   *  Internal method for fold(), which performs the actual folding.
   *       Should be implemented in subclasses having any internal structure.
   *
   * @protected
   * @param initial
   * @param combine
   */
  _fold<T> (initial:T, combine: (acc: T, expr: Expr) => TraverseValue<T>): TraverseValue<T> {
    return combine(initial, this);
  }

  /**
   * @experimental
   *   Fold an application tree bottom to top.
   *        For each subtree, the function is given the term in the root position and
   *        a list of the results of folding its arguments.
   *
   *        E.g. fold('x y (z t)', f) results in f(x, [f(y, []), f(z, [f(t, [])])])
   *
   * @example expr.foldBottomUp((head, tail) => {
   *    if (head.arity && head.arity <= tail.length) {
   *      return '(<span class="redex">'
   *          + head + ' '
   *          + tail.slice(0, head.arity).join(' ')
   *          + '</span>'
   *          + tail.slice(head.arity).join(' ')
   *          + ')';
   *    } else {
   *       return '(' + head + ' ' + tail.join(' ') + ')';
   *    }
   * });
   * @template T
   * @param {(head: Expr, tail: T[]) => T} fun
   * @return {T}
   */
  foldBottomUp<T> (fun: (head: Expr, tail: T[]) => T): T {
    const [head, ...tail] = this.unroll();
    return fun(head, tail.map(e => e.foldBottomUp(fun)));
  }

  /**
   *  Try to empirically find an equivalent lambda term for the expression,
   *       returning also the term's arity and some other properties.
   *
   *       This is used internally when declaring a Primitive / Alias term,
   *       unless {canonize: false} is used.
   *
   *       As of current it only recognizes terms that have a normal form,
   *       perhaps after adding some variables. This may change in the future.
   *
   *       Use extras.toLambda() if you want to get a lambda term in any case.
   *
   * @sealed
   * @param {{max?: number, maxArgs?: number}} options
   * @return {TermInfo}
   */
  infer (options : InferOptions = {}): TermInfo {
    // First gather the names which can collide with generated variables and forbid them.
    // TODO confusing naming, rewrite.
    const skipNames: Record<string, boolean> = {};
    const skipSkip: Set<Expr> = new Set();
    this.traverse(e => {
      if (e instanceof Named && !skipSkip.has(e))
        skipNames[e.name] = true;
      if (e instanceof Lambda)
        skipSkip.add(e.arg);
    });
    return this._infer({
      max:     options.max ?? DEFAULTS.max,
      maxArgs: options.maxArgs ?? DEFAULTS.maxArgs,
      maxSize: options.maxSize ?? DEFAULTS.maxSize,
      skipNames,
    }, 0);
    // TODO maybe traverse() if no normal form was found
  }

  /**
   *  Internal method for infer(), which performs the actual inference.
   * @param {{max: number, maxArgs: number}} options
   * @param {number} nargs - var index to avoid name clashes
   * @returns {TermInfo}
   * @private
   */
  _infer (options:{max: number, maxArgs: number, maxSize: number, skipNames: Record<string, boolean>}, nargs: number): TermInfo {
    const probe: FreeVar[] = [];
    let steps = 0;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let expr: Expr = this;
    // eslint-disable-next-line no-labels
    main: for (let i = 0; i < options.maxArgs; i++) {
      // keep reduction budget at bay.
      // ideally should be increased with more args but that (predictably) causes very long test runs
      const next = expr.run({ max: Math.max((options.max - steps) / 2, 10), maxSize: options.maxSize });
      // console.log(`infer step ${i}, expr = ${expr}, probe = [${probe}]: `, next);
      steps += next.steps;

      // if !final, still append more args, e.g. CK(WWW) is a->a, not CK(omega)
      if (next.final && firstVar(next.expr)) {
        // can't append more variables, return or recurse
        expr = next.expr;
        if (!expr.any(e => !(e instanceof FreeVar || e instanceof App)))
          return maybeLambda(probe, expr, { steps });
        const list = expr.unroll();
        let discard = false;
        let duplicate = false;
        const acc = [];
        for (let j = 1; j < list.length; j++) {
          const sub = list[j]._infer(
            { maxArgs: options.maxArgs - nargs, max: options.max - steps, maxSize: options.maxSize, skipNames: options.skipNames }, // limit recursion
            nargs + i // avoid variable name clashes
          );
          steps += sub.steps ?? 0;
          if (!sub.expr)
            // eslint-disable-next-line no-labels
            break main; // press f to pay respects
          if (sub.discard)
            discard = true;
          if (sub.duplicate)
            duplicate = true;
          acc.push(sub.expr);
        }
        return maybeLambda(probe, list[0].apply(...acc), { discard, duplicate, steps });
      }
      while (options.skipNames[nthvar(nargs + i).name])
        nargs++;
      const push = nthvar(nargs + i);
      probe.push(push);
      expr = next.expr.apply(push);
    }
    return { normal: false, proper: false, steps };
  }

  /**
   *  Expand an expression into a list of terms
   * that give the initial expression when applied from left to right:
   * ((a, b), (c, d)) => [a, b, (c, d)]
   *
   * This can be thought of as an opposite of apply:
   * fun.apply(...arg).unroll() is exactly [fun, ...args]
   * (even if ...arg is in fact empty).
   *
   * @returns {Expr[]}
   */
  unroll (): Expr[] {
    // currently only used by infer() but may be useful
    // to convert binary App trees to n-ary or smth
    return [this];
  }

  /**
   * Replace all instances of `search` in the expression with `replace` and return the resulting expression,
   * or null if no changes could be made.
   *
   * Lambda terms and applications will never match if used as plug
   * as they are impossible to compare without extensive computations.
   *
   * Typically used on variables but can also be applied to other terms, e.g. aliases.
   * See also Expr.traverse() for more flexible replacement of subterms.
   *
   * @param {Expr} search
   * @param {Expr} replace
   * @return {Expr|null}
   */
  subst (search:Expr, replace:Expr): Expr|null {
    return this === search ? replace : null;
  }

  /**
   *  Apply term reduction rules, if any, to the given argument.
   * A returned value of null means no reduction is possible.
   * A returned value of Expr means the reduction is complete and the application
   *     of this and arg can be replaced with the result.
   * A returned value of a function means that further arguments are needed,
   *     and can be cached for when they arrive.
   *
   * This method is between apply() which merely glues terms together,
   *     and step() which reduces the whole expression.
   *
   * foo.invoke(bar) is what happens inside foo.apply(bar).step() before
   *     reduction of either foo or bar is attempted.
   *
   * The name 'invoke' was chosen to avoid confusion with either 'apply' or 'reduce'.
   *
   * @param {Expr} arg
   * @returns {Partial | null}
   */
  invoke (arg:Expr): Invocation | null {
    return null;
  }

  /**
   *  iterate one step of a calculation.
   * @return {{expr: Expr, steps: number, changed: boolean}}
   */
  step (): Step { return { expr: this, steps: 0, changed: false } }

  /**
   * Iteratively apply {@link step} to the expression until it's irreducible,
   * or until the provided limits are reached.
   *
   * Returns { expr: Expr, steps: number, final: boolean }.
   *
   * If { throw: true } is given and no irreducible form was reached within the limits, an error is thrown.
   *
   * @sealed
   * @param {{max?: number, steps?: number, throw?: boolean}|Expr} [opt]
   * @param {Expr} args
   * @return {{expr: Expr, steps: number, final: boolean}}
   */
  run (opt : RunOptions|Expr = {}, ...args: Expr[]): Run {
    if (opt instanceof Expr) {
      args.unshift(opt);
      opt = {};
    }
    let expr = args ? this.apply(...args) : this;
    let steps = opt.steps ?? 0;
    // make sure we make at least 1 step, to tell whether we've reached the normal form
    const max = Math.max(opt.max ?? DEFAULTS.max, 0) + steps;
    let final = false;
    while (true) {
      const next = expr.step();
      if (!next.changed) {
        final = true;
        break;
      }
      if ((next.expr.size ?? 1) > (opt.maxSize ?? DEFAULTS.maxSize))
        break;
      steps += next.steps;
      expr = next.expr;
      if (steps >= max)
        break;
    }
    if (opt.throw && !final)
      throw new Error('Failed to compute expression in ' + max + ' steps');
    return { final, steps, expr };
  }

  /**
   * Returns an iterator of reduction steps of the expression, up to the provided limits.
   * Each step is an object of { expr, steps, final }, same as in {@link run}.
   *
   * Mnemonics: like {@link run} but slower.
   *
   * @remarks
   * This method is final. Do not override, override {@link step} instead.
   *
   * @param {{max?: number}} options
   * @return {IterableIterator<{final: boolean, expr: Expr, steps: number}>}
   */
  * walk (options : RunOptions = {}): IterableIterator<Run> {
    let steps = options.steps ?? 0;
    const max = Math.max(options.max ?? Infinity, 1) + steps;
    const maxSize = options.maxSize ?? DEFAULTS.maxSize;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let expr:Expr = this;
    let final = false;

    // guaranteed to yield at least once
    while (true) {
      // 1. calculate
      // 2. yield _unchanged_ expression
      // 3. either advance or stop
      const next = expr.step();
      if (!next.changed)
        final = true;
      yield { expr, steps, final };
      if (final || steps >= max || (next.expr.size ?? 1) > maxSize)
        break;
      steps += next.steps;
      expr = next.expr;
    }
  }

  /**
   *  True is the expressions are identical, false otherwise.
   *       Aliases are expanded.
   *       Bound variables in lambda terms are renamed consistently.
   *       However, no reductions are attempted.
   *
   *       E.g. a->b->a == x->y->x is true, but a->b->a == K is false.
   *
   * @param {Expr} other
   * @return {boolean}
   */
  equals (other:Expr):boolean {
    if (this === other)
      return true;
    if (other instanceof Alias)
      return other.impl.equals(this);
    return false;
  }

  /**
   *  Assert expression equality. Can be used in tests.
   *
   * `this` is the expected value and the argument is the actual one.
   * Mnemonic: the expected value is always a combinator, the actual one may be anything.
   *
   * In case of failure, an error is thrown with a message describing the first point of difference
   * and `expected` and `actual` properties like in AssertionError.
   * AssertionError is not used directly because browsers don't recognize it.
   *
   * @sealed
   * @param {Expr} actual
   * @param {string} comment
   */
  expect (actual:Expr|unknown, comment = ''):void {
    // not adding `asserts actual is Expr` because it doesn't work with `parse(foo).expect(bar)` in tests
    comment = comment ? comment + ': ' : '';
    if (!(actual instanceof Expr)) {
      throw new Error(comment + 'Expected a combinator but found '
        + (actual?.constructor?.name ?? typeof actual));
    }
    if (this.equals(actual))
      return; // all good

    // TODO wanna use AssertionError but browser doesn't recognize it
    // still the below hack works for mocha-based tests.
    const poorMans = new Error(comment + `${this} does not equal ${actual}`) as Error & { expected?: string, actual?: string };
    poorMans.expected = this.diag();
    poorMans.actual = actual.diag();
    throw poorMans;
  }

  /**
   *       Returns string representation of the expression.
   *       Same as format() without options.
   *
   *       Use formatImpl() to override in subclasses.
   * @return {string}
   * @sealed
   */
  toString (): string {
    return this.format();
  }

  /**
   * Whether the expression needs to be parenthesized when printed in terse mode.
   * (see {@link format})
   * @param isFirst whether this is the first term in a sequence
   * @return {boolean}
   * @protected
   */
  _braced (isFirst?:boolean): boolean {
    return false;
  }

  /**
   *  Whether the expression can be printed without a space when followed by arg.
   * @param {Expr} arg
   * @returns {boolean}
   * @protected
   */
  _unspaced (arg: Expr): boolean {
    return this._braced(true);
  }

  /**
   *          Stringify the expression with fancy formatting options.
   *          Said options mostly include wrappers around various constructs in form of ['(', ')'],
   *          as well as `terse` and `html` flags that fill in appropriate defaults.
   *          Format without options is equivalent to toString() and can be parsed back.
   *
   * @sealed
   *
   * @param   {Object} [options]  - formatting options
   * @param   {boolean} [options.terse]   - whether to use terse formatting (omitting unnecessary spaces and parentheses)
   * @param   {boolean} [options.html]    - whether to default to HTML tags & entities.
   *                                If a named term has fancyName property set, it will be used instead of name in this mode.
   * @param   {[string, string]} [options.brackets]  - wrappers for application arguments, typically ['(', ')']
   * @param   {[string, string]} [options.var]       - wrappers for variable names
   *                                (will default to &lt;var&gt; and &lt;/var&gt; in html mode).
   * @param   {[string, string, string]} [options.lambda]    - wrappers for lambda abstractions, e.g. ['&lambda;', '.', '']
   *                                where the middle string is placed between argument and body
   *                                default is ['', '->', ''] or ['', '-&gt;', ''] for html
   * @param   {[string, string]} [options.around]    - wrappers around (sub-)expressions.
   *                                individual applications will not be wrapped, i.e. (a b c) but not ((a b) c)
   * @param   {[string, string]} [options.redex]     - wrappers around the starting term(s) that have enough arguments to be reduced
   * @param   {Object<string, Expr>} [options.inventory]     - if given, output aliases in the set as their names
   *                                and any other aliases as the expansion of their definitions.
   *                                The default is a cryptic and fragile mechanism dependent on a hidden mutable property.
   * @returns {string}
   *
   * @example foo.format() // equivalent to foo.toString()
   * @example foo.format({terse: false}) // spell out all parentheses
   * @example foo.format({html: true}) // use HTML tags and entities
   * @example foo.format({ around: ['(', ')'], brackets: ['', ''], lambda: ['(', '->', ')'] }) // lisp style, still back-parsable
   * @example foo.format({ lambda: ['&lambda;', '.', ''] }) // pretty-print for the math department
   * @example foo.format({ lambda: ['', '=>', ''], terse: false }) // make it javascript
   * @example foo.format({ inventory: { T } }) // use T as a named term, expand all others
   *
   */
  format (options: FormatOptions = {}): string {
    if (options.inventory) {
      const { inventory, ...format } = options;
      // replace all aliases except those in the inventory with their implementation
      // TODO we ignore inline aliases, could make a detour later
      return (this.traverse({}, e => {
        if (e instanceof Alias && inventory[e.name] !== e)
          return control.redo(e.impl);
      }) ?? this).format(format);
    }

    const fallback: RefinedFormatOptions = options.html
      ? {
        brackets: ['(', ')'],
        space:    ' ',
        var:      ['<var>', '</var>'],
        lambda:   ['', '-&gt;', ''],
        around:   ['', ''],
        redex:    ['', ''],
      }
      : {
        brackets: ['(', ')'],
        space:    ' ',
        var:      ['', ''],
        lambda:   ['', '->', ''],
        around:   ['', ''],
        redex:    ['', ''],
      };

    return this.formatImpl({
      terse:    options.terse    ?? true,
      brackets: options.brackets ?? fallback.brackets,
      space:    options.space    ?? fallback.space,
      var:      options.var      ?? fallback.var,
      lambda:   options.lambda   ?? fallback.lambda,
      around:   options.around   ?? fallback.around,
      redex:    options.redex    ?? fallback.redex,
      html:     options.html     ?? false,
    }, 0);
  }

  /**
   *  Internal method for format(), which performs the actual formatting.
   * @param {Object} options
   * @param {number} nargs
   * @returns {string}
   * @protected
   * @abstract
   */
  abstract formatImpl (options: RefinedFormatOptions, nargs: number): string;

  /**
   *  Returns a string representation of the expression tree, with indentation to show structure.
   *
   *       Applications are flattened to avoid excessive nesting.
   *       Variables include ids to distinguish different instances of the same variable name.
   *
   *       May be useful for debugging.
   *
   * @returns {string}
   *
   * @example
   * > console.log(ski.parse('C 5 x (x->x x)').diag())
   * App:
   *   Primitive: C
   *   Church: 5
   *   FreeVar: x[53]
   *   Lambda (x[54]):
   *     App:
   *       FreeVar: x[54]
   *       FreeVar: x[54]
   */
  diag (indent: string = ''): string {
    return indent + this.constructor.name + ': ' + this;
  }

  declare (options: FormatOptions = {}): string {
    // TODO also make declaration syntax configurable
    const { declaration = ['', '=', '; '], ...format }  = options;

    const res = toposort({ list: [this], env: format.inventory });

    return res.list
      .map(s => s.declareImpl({ ...format, inventory: res.env, declaration }))
      .join(declaration[2]);
  }

  declareImpl (options: FormatOptions): string {
    return this.format(options);
  }

  /**
   * Convert the expression to a JSON-serializable format.
   * Sadly the format is not yet finalized and may change in the future.
   *
   * @experimental
   * @sealed
   */
  toJSON (): string | object {
    return this.declare();
  }
}

/**
 *  Application of two {@link Expr} terms.
 *
 *  Never ever call `new App(fun, arg)` directly, use `fun.apply(...args)` instead.
 */
export class App extends Expr {
  fun: Expr;
  arg: Expr;

  /** If irreducible, cache it and don't try anymore */
  final?: boolean;

  /**
   * @param fun
   * @param arg
   */
  constructor (fun:Expr, arg:Expr) {
    super();
    this.arg = arg;
    this.fun = fun;
    this.size = fun.size + arg.size;
  }

  _traverse_descend (options: TraverseOptions, change: TraverseCallback): TraverseValue<Expr> {
    const [fun, fAction] = unwrap(this.fun._traverse_redo(options, change));
    if (fAction === control.stop)
      return control.stop(fun ? fun.apply(this.arg) : null);

    const [arg, aAction] = unwrap(this.arg._traverse_redo(options, change));

    const final:Expr|null = (fun || arg) ? (fun ?? this.fun).apply(arg ?? this.arg) : null;
    if (aAction === control.stop)
      return control.stop(final);
    return final;
  }

  any (predicate: (e: Expr) => boolean): boolean {
    return predicate(this) || this.fun.any(predicate) || this.arg.any(predicate);
  }

  _fold<T> (initial:T, combine: (acc: T, expr: Expr) => TraverseValue<T>): TraverseValue<T> {
    const [value = initial, action = 'descend'] = unwrap(combine(initial, this));
    if (action === control.prune)
      return value;
    if (action === control.stop)
      return control.stop(value);
    const [fValue = value, fAction = 'descend'] = unwrap(this.fun._fold(value, combine));
    if (fAction === control.stop)
      return control.stop(fValue);
    const [aValue = fValue, aAction = 'descend'] = unwrap(this.arg._fold(fValue, combine));
    if (aAction === control.stop)
      return control.stop(aValue);
    return aValue;
  }

  subst (search: Expr, replace:Expr): Expr | null {
    const fun = this.fun.subst(search, replace);
    const arg = this.arg.subst(search, replace);

    return (fun || arg) ? (fun ?? this.fun).apply(arg ?? this.arg) : null;
  }

  /**
   * @return {{expr: Expr, steps: number}}
   */

  step (): Step {
    // normal reduction order: first try root, then at most 1 step
    if (!this.final) {
      // try to apply rewriting rules, if applicable, at first
      const partial = this.fun.invoke(this.arg);
      if (partial instanceof Expr)
        return { expr: partial, steps: 1, changed: true };
      else if (typeof partial === 'function')
        this.invoke = partial; // cache for next time

      // descend into the leftmost term
      const fun = this.fun.step();
      if (fun.changed)
        return { expr: fun.expr.apply(this.arg), steps: fun.steps, changed: true };

      // descend into arg
      const arg = this.arg.step();
      if (arg.changed)
        return { expr: this.fun.apply(arg.expr), steps: arg.steps, changed: true };

      // mark as irreducible
      this.final = true; // mark as irreducible at root
    }

    return { expr: this, steps: 0, changed: false };
  }

  invoke (arg:Expr): Invocation | null {
    // propagate invocation towards the root term,
    // caching partial applications as we go
    const partial = this.fun.invoke(this.arg);
    if (partial instanceof Expr)
      return partial.apply(arg);
    else if (typeof partial === 'function') {
      this.invoke = partial;
      return partial(arg);
    } else {
      // invoke = null => we're uncomputable, cache for next time
      this.invoke = (arg: Expr) => null;
      return null;
    }
  }

  unroll (): Expr[] {
    return [...this.fun.unroll(), this.arg];
  }

  equals (other: Expr): boolean {
    if (!(other instanceof App))
      return super.equals(other);
    return this.fun.equals(other.fun) && this.arg.equals(other.arg);
  }

  _braced (isFirst?: boolean): boolean {
    return !isFirst;
  }

  formatImpl (options:RefinedFormatOptions, nargs: number): string {
    const fun = this.fun.formatImpl(options, nargs + 1);
    const arg = this.arg.formatImpl(options, 0);
    const wrap = nargs ? ['', ''] : options.around!;
    // TODO ignore terse for now
    if (options.terse && !this.arg._braced(false))
      return wrap[0] + fun + (this.fun._unspaced(this.arg) ? '' : options.space) + arg + wrap[1];
    else
      return wrap[0] + fun + options.brackets![0] + arg + options.brackets![1] + wrap[1];
  }

  diag (indent: string = ''): string {
    return indent + 'App:\n' + this.unroll().map(e => e.diag(indent + '  ')).join('\n');
  }

  _unspaced (arg: Expr): boolean {
    return this.arg._braced(false) ? true : this.arg._unspaced(arg);
  }
}

/**
 *  An abstract class representing a named term.
 *
 * @param {String} name
 */
export class Named extends Expr {
  name: string;
  fancyName?: string;

  constructor (name: string) {
    super();
    if (typeof name !== 'string' || name.length === 0)
      throw new Error('Attempt to create a named term with improper name');
    this.name = name;
  }

  _unspaced (arg: Expr): boolean {
    return !!(
      (arg instanceof Named) && (
        (this.name.match(/^[A-Z+]$/) && arg.name.match(/^[a-z+]/i))
          || (this.name.match(/^[a-z+]/i) && arg.name.match(/^[A-Z+]$/))
      )
    );
  }

  formatImpl (options: RefinedFormatOptions, nargs: number): string {
    // NOTE fancyName is not yet official and may change name or meaning
    const name = options.html ? this.fancyName ?? this.name : this.name;
    return this.arity !== undefined && this.arity > 0 && this.arity <= nargs
      ? options.redex![0] + name + options.redex![1]
      : name;
  }
}

/** A synthetic key for debugging/distinguishing variables. */
let freeId = 0;

/**
 * A named variable.
 *
 * Given the functional nature of combinatory logic, variables are treated
 * as functions that we don't know how to evaluate just yet.
 *
 * Two variables are considered the same iff they have the same `name` and `scope` properties.
 * If `scope` is not given, the variable is only equal to itself.
 *
 * By convention, FreeVar.global is a constant denoting a global unbound variable.
 */
export class FreeVar extends Named {
  /**
   * @param name - name of the variable
   * @param scope - an object representing where the variable belongs to.
   */
  scope?: object;
  id: number;
  constructor (name:string, scope?: object ) {
    super(name);
    this.id = ++freeId;
    // TODO replace with null and scope??[notmatching] everywhere, but later
    this.scope = scope === undefined ? this : scope;
  }

  equals (other: Expr): boolean {
    if (!(other instanceof FreeVar))
      return super.equals(other);
    return this.name === other.name && this.scope === other.scope;
  }

  subst (search: Expr, replace: Expr): Expr | null {
    if (search instanceof FreeVar && search.name === this.name && search.scope === this.scope)
      return replace;
    return null;
  }

  formatImpl (options: RefinedFormatOptions, nargs: number): string {
    const name = options.html ? this.fancyName ?? this.name : this.name;
    return options.var![0] + name + options.var![1];
  }

  declareImpl (options: FormatOptions): string {
    return options.declaration![0] + this.format(options) + options.declaration![1];
  }

  diag (indent: string = ''): string {
    // include variable id for disambiguation
    return `${indent}FreeVar: ${this.name}[${this.id}]`;
  }

  static global = ['global'];
}

/**
 *  A named term with a rewriting rule implemented in JavaScript/TypeScript.
 *
 *  'impl' is a function with the signature Expr => Expr => ... => Expr
 *  (see typedef Invocation).
 *
 *  Note that as of current something like a=>b=>b(a) is not possible,
 *  use full form instead: a=>b=>b.apply(a).
 *
 * @example new Primitive('K', x => y => x); // constant
 * @example new Primitive('Y', function(f) { return f.apply(this.apply(f)); }); // self-application
 */
export class Primitive extends Named {
  /**
   * @param name Name of the term
   * @param impl A javascript implementation of the term's rewriting rule.
   * @param opt Optional settings.
   */
  constructor (name: string, impl: (e: Expr) => Invocation,
    opt: {note?: string, arity?: number, canonize?: boolean } = {}) {
    super(name);
    // setup essentials
    this.invoke  = impl;

    this.annotate({ canonize: true, ...opt });
  }
}

export class Atomic extends Primitive {
  source: Expr;
  /** the placeholder variable used for self-reference within `source`, if any */
  selfRef?: FreeVar;

  constructor (self: FreeVar|string, impl: Lambda) {
    if (typeof self === 'string')
      self = new FreeVar(self);
    const { code, env, arity, selfRef } = lambda2code(self, impl);

    // console.log('Atomic: generated code for ' + self.name + ':\n' + code);

    const invoke = new Function('env', code)(env) as (e: Expr) => Invocation; // eslint-disable-line no-new-func

    super(self + '', invoke, {
      arity,
      canonize: false,
      note:     impl.format({ html: true, lambda: ['', ' &mapsto; ', ''] }),
    });
    this.source = impl;
    if (selfRef)
      this.selfRef = self;
  }

  declareImpl (options: FormatOptions): string {
    return options.declaration![0]
      + '@atomic ' + this.format(options)
      + options.declaration![1] + this.source.format(options);
  }

  equals (other: Expr): boolean {
    if (!(other instanceof Atomic))
      return super.equals(other);
    if (this.name !== other.name)
      return false;
    if (this.selfRef && other.selfRef)
      return this.source.equals(other.source.subst(other.selfRef, this.selfRef) ?? other.source);
    if (!this.selfRef && !other.selfRef)
      return this.source.equals(other.source);
    return false;
  }

  diag ( indent: string = ''): string {
    return `${indent}Atomic ${this.name} = ${this.source}\n`;
  }
}

/**
 *  A lambda abstraction.
 *
 *  Takes an arg: {@link FreeVar} and an impl: {@link Expr}.
 *  Upon evaluation, all occurrences of `arg` within `impl`
 *  will be replaced by the given term.
 *
 *  Note that 'arg' will be replaced by a localized placeholder,
 *  so the original variable can be used elsewhere without interference.
 */
export class Lambda extends Expr {
  arg: FreeVar;
  impl: Expr;
  /**
   * @param {FreeVar} arg
   * @param {Expr} impl
   */
  constructor (arg: FreeVar, impl: Expr) {
    super();

    if (!(arg instanceof FreeVar))
      throw new Error('Lambda argument must be a FreeVar');

    // localize argument variable and bind it to oneself
    const local = new FreeVar(arg.name, this);
    this.arg = local;
    this.impl = impl.subst(arg, local) ?? impl;
    this.arity = 1;
    this.size = impl.size + 1;
  }

  invoke (arg: Expr): Expr {
    return this.impl.subst(this.arg, arg) ?? this.impl;
  }

  _traverse_descend (options: TraverseOptions, change: TraverseCallback): TraverseValue<Expr> {
    // alas no proper shielding of self.arg is possible
    const [impl, iAction] = unwrap(this.impl._traverse_redo(options, change));

    const final = impl ? new Lambda(this.arg, impl) : null;

    return iAction === control.stop ? control.stop(final) : final;
  }

  any (predicate: (e: Expr) => boolean): boolean {
    return predicate(this) || this.impl.any(predicate);
  }

  _fold<T> (initial:T, combine: (acc: T, expr: Expr) => TraverseValue<T>): TraverseValue<T> {
    const [value = initial, action = 'descend'] = unwrap(combine(initial, this));
    if (action === control.prune)
      return value;
    if (action === control.stop)
      return control.stop(value);
    const [iValue, iAction] = unwrap(this.impl._fold(value, combine));
    if (iAction === control.stop)
      return control.stop(iValue);
    return iValue ?? value;
  }

  subst (search: Expr, replace:Expr): Expr | null {
    if (search === this.arg)
      return null;
    const change = this.impl.subst(search, replace);
    return change ? new Lambda(this.arg, change) : null;
  }

  equals (other: Expr): boolean {
    if (!(other instanceof Lambda))
      return super.equals(other);

    const t = new FreeVar('t'); // TODO better placeholder name

    return this.invoke(t).equals(other.invoke(t));
  }

  formatImpl (options: RefinedFormatOptions, nargs: number): string {
    return (nargs > 0 ? options.brackets![0] : '')
      + options.lambda![0]
      + this.arg.formatImpl(options, 0) // TODO highlight redex if nargs > 0
      + options.lambda![1]
      + this.impl.formatImpl(options, 0) + options.lambda![2]
      + (nargs > 0 ? options.brackets![1] : '');
  }

  diag (indent: string = ''): string {
    return `${indent}Lambda (${this.arg.name}[${this.arg.id}]):\n`
      + this.impl.diag(indent + '  ');
  }

  _braced (isFirst: boolean): boolean {
    return true;
  }
}

/**
 *  Church numeral representing non-negative integer `n`:
 *  `n f x = f(f(...(f x)...))` with `f` applied `n` times.
 */
export class Church extends Expr {
  n: number;
  /**
   * @param {number} n
   */
  constructor (n: number) {
    n = Number.parseInt(String(n));
    if (!(n >= 0))
      throw new Error('Church number must be a non-negative integer');
    super();
    this.invoke = x => y => {
      let expr = y;
      for (let i = n; i-- > 0; )
        expr = x.apply(expr);
      return expr;
    };

    /** @type {number} */
    this.n = n;
    this.arity = 2;
  }

  equals (other: Expr): boolean {
    if (!(other instanceof Church))
      return super.equals(other);
    return this.n === other.n;
  }

  _unspaced (arg: Expr): boolean {
    return false;
  }

  formatImpl (options: RefinedFormatOptions, nargs: number): string {
    return nargs >= 2
      ? options.redex![0] + this.n + options.redex![1]
      : this.n + '';
  }
}

function waitn (n: number): (e : Expr) => (arg: Expr) => Invocation {
  return n <= 1
    ? (e: Expr) => (arg: Expr) => e.apply(arg)
    : (e: Expr) => (arg: Expr) => waitn(n - 1)(e.apply(arg));
}

/**
 *  A named alias for an existing expression.
 *
 * Aliasing allows declaring new terms without a native implementation.
 * This is what happens when one writes `B = S(KS)K` in the interpreter.
 *
 * Aliases are transparent in terms of `equals` and `expect`;
 * for that reason, Alias.diag() in not adding to the indentation.
 *
 * Aliases have an `inline` property. Tf true, the alias will be replaced with its implementation
 * everywhere, unless specifically told otherwise e.g. by { inventory: { ... } } option of format().
 *
 * Upon creation, the aliases arity is calculated (unless `canonize` is false).
 *
 * Upon evaluation, the alias will be replaced with its implementation,
 * _unless_ it's not inline and has positive arity,
 * in which case it will wait for the required number of arguments before such replacement.
 */
export class Alias extends Named {
  /**
   * @param name
   * @param impl
   * @param options
   */
  impl: Expr;
  inline?: boolean;

  constructor (
    name: string,
    impl: Expr,
    options : {canonize?: boolean, max?: number, maxArgs?: number, note?: string, inline?: boolean, arity?: number} = {}
  ) {
    super(name);
    if (!(impl instanceof Expr))
      throw new Error('Attempt to create an alias for a non-expression: ' + impl);
    this.impl = impl;

    this.annotate({ arity: impl.arity, ...options });
    this.invoke = waitn(options.inline ? 0 : this.arity ?? 0)(impl);
    if (options.inline) {
      this.inline = true;
      this.size = impl.size;
    }
  }

  /**
   * Make the alias inline, i.e. replace it with its implementation everywhere.
   *
   * NOTE: this is a mutating method, and mutation has a terrible track record
   * throughout this codebase.
   *
   * Replaces the old `outdated` attribute.
   * Used by the parser when a term definition is removed or updated.
   *
   * May change in future versions, use with caution.
   *
   * @experimental
   * @returns {this}
   */
  makeInline (): this {
    this.invoke = waitn(0)(this.impl);
    this.inline = true;
    this.size = this.impl.size;
    return this;
  }

  /**
   * @property {boolean} [proper] - whether the alias is a proper combinator (i.e. contains no free variables or constants)
   * @property {number} [arity] - the number of arguments the alias waits for before expanding
   * @property {Expr} [canonical] - equivalent lambda term.
   */

  _traverse_descend (options: TraverseOptions, change: TraverseCallback): TraverseValue<Expr> {
    return this.impl._traverse_redo(options, change);
  }

  any (predicate: (e: Expr) => boolean): boolean {
    return predicate(this) || this.impl.any(predicate);
  }

  _fold<T> (initial:T, combine: (acc: T, expr: Expr) => TraverseValue<T>): TraverseValue<T> {
    const [value = initial, action] = unwrap(combine(initial, this));
    if (action === control.prune)
      return value;
    if (action === control.stop)
      return control.stop(value);
    const [iValue, iAction] = unwrap(this.impl._fold(value, combine));
    if (iAction === control.stop)
      return control.stop(iValue);
    return iValue ?? value;
  }

  subst (search: Expr, replace: Expr): Expr | null {
    if (this === search)
      return replace;
    return this.impl.subst(search, replace);
  }

  // DO NOT REMOVE TYPE or tsc chokes with
  //       TS2527: The inferred type of 'Alias' references an inaccessible 'this' type.
  /**
   * @return {{expr: Expr, steps: number, changed: boolean}}
   */
  step (): Step {
    // arity known = waiting for args to expand
    if ((this.arity ?? 0) > 0)
      return { expr: this, steps: 0, changed: false };
    // expanding is a change but it takes 0 steps
    return { expr: this.impl, steps: 0, changed: true };
  }

  equals (other: Expr): boolean {
    if (this === other)
      return true;
    return other.equals(this.impl);
  }

  _braced (isFirst: boolean): boolean {
    return this.inline ? this.impl._braced(isFirst) : false;
  }

  formatImpl (options: RefinedFormatOptions, nargs: number): string {
    return this.inline ? this.impl.formatImpl(options, nargs) : super.formatImpl(options, nargs);
  }

  declareImpl (options: FormatOptions): string {
    const d = options.declaration!;
    return d[0] + this.name + d[1] + this.impl.format(options);
  }

  diag (indent: string = ''): string {
    // no indent increase so that a diff between diags is consistent with how `equals` works.
    return `${indent}Alias (${this.name})${this.inline ? '[inline]' : ''}: \\\n` + this.impl.diag(indent);
  }
}

// ----- Expr* classes end here -----

// utility functions dependent on Expr* classes, in alphabetical order

function firstVar (expr: Expr) {
  // yay premature optimization
  while (expr instanceof App)
    expr = expr.fun;
  return expr instanceof FreeVar;
}

export function lambda2code (self: FreeVar, impl: Lambda):
  { code: string, env: Record<string, Expr>, arity: number, selfRef: boolean } {
  const selfRef = impl.any(e => e === self);

  // map content:
  //     self=>self - placeholder for `this`
  //     argN=>argN - placeholders for the resulting function's arguments
  //     (expr)=>termN - placeholder for constant expressions (not containing self/argN)
  //          for these, env[map[expr].name] === expr
  const env: Record<string, Expr> = {};
  const map: Map<Expr, FreeVar> = new Map();

  if (selfRef)
    map.set(self, self);

  // collect lambda arguments
  const args: FreeVar[] = [];
  let body: Expr = impl;
  while (body instanceof Lambda) {
    const placeholder = new FreeVar('arg' + args.length);
    map.set(body.arg, placeholder);
    args.push(placeholder);
    body = body.impl;
  }

  // a node "depends" on self/argN if it (or one of its subterms) is exactly
  // one of the placeholders we already know about.
  const known: Set<Expr> = new Set(map.keys());
  const dependsOnKnown = (e: Expr): boolean => e.any(sub => known.has(sub));

  // replace all terms independent of self/argN with placeholders, and add them to env.
  // termNames remembers already-assigned names for a given (identical) subterm,
  // so the same constant isn't added to env twice.
  let termCount = 0;
  const termNames: Map<Expr, string> = new Map();

  const gen = (e: Expr): string => {
    if (map.has(e)) {
      const placeholder = map.get(e)!;
      return placeholder === self ? 'self' : placeholder.name;
    }
    if (!dependsOnKnown(e)) {
      let name = termNames.get(e);
      if (name === undefined) {
        name = 'term' + (termCount++);
        termNames.set(e, name);
        env[name] = e;
      }
      return name;
    }
    if (e instanceof App)
      return gen(e.fun) + '.apply(' + gen(e.arg) + ')';
    if (e instanceof Lambda)
      return 'new env.Lambda(' + gen(e.arg) + ', ' + gen(e.impl) + ')';
    // should be unreachable: any node not covered above must be either
    // a known placeholder or independent of self/argN, both handled above.
    throw new Error('Cannot generate code for term: ' + e);
  };

  const bodyCode = gen(body);

  // needed by the generated code to construct `new env.Lambda(arg, impl)` for nested lambdas;
  // Function() bodies aren't lexically scoped, so it has to be passed in via env.
  (env as Record<string, unknown>).Lambda = Lambda;

  // generate preamble generating var from termN's
  let preamble = '';
  for (const name of termNames.values())
    preamble += 'var ' + name + ' = env.' + name + ';\n';

  // generate function signature from args[], optionally insert `var self=this;`
  let code = 'return function(' + args[0].name + ') {\n';
  if (selfRef)
    code += 'var self = this;\n';
  for (let i = 1; i < args.length; i++)
    code += 'return function(' + args[i].name + ') {\n';

  // generate function body from body, replacing application with foo.apply(bar)
  //      and lambdas with new Lambda(arg, impl)
  code += 'return ' + bodyCode + ';\n';

  for (let i = 1; i < args.length; i++)
    code += '};\n';
  code += '};\n';

  // combine all three into `const code`
  code = preamble + code;

  return { code, env, arity: args.length, selfRef };
}

/**
 * @private
 * Given a list of free variables, an expression, and some capabilities of the context,
 * return either a lambda term, or the original expression if no lambda abstraction is needed,
 * plus some metadata about the term and the context.
 *
 * Used by infer() internally.
 *
 * @param {FreeVar[]} args
 * @param {Expr} expr
 * @param {object} caps
 * @returns {TermInfo}
 */
function maybeLambda (args: FreeVar[], expr: Expr, caps : {discard?: boolean, duplicate?: boolean, steps: number}): TermInfo {
  const count = new Array(args.length).fill(0);
  let proper = true;
  expr.traverse(e => {
    if (e instanceof FreeVar) {
      const index = args.findIndex(a => a.name === e.name);
      if (index >= 0) {
        count[index]++;
        return;
      }
    }
    if (!(e instanceof App))
      proper = false;
  });

  const skip : Set<number> = new Set();
  const dup : Set<number> = new Set();
  for (let i = 0; i < args.length; i++) {
    if (count[i] === 0)
      skip.add(i);
    else if (count[i] > 1)
      dup.add(i);
  }

  for (let i = args.length; i-- > 0; )
    expr = new Lambda(args[i], expr);

  return {
    normal:    true,
    steps:     caps.steps,
    expr,
    arity:     args.length,
    ...(skip.size ? { skip } : {}),
    ...(dup.size ? { dup } : {}),
    duplicate: !!dup.size  || caps.duplicate || false,
    discard:   !!skip.size || caps.discard   || false,
    proper,
  };
}

function nthvar (n: number): FreeVar {
  return new FreeVar('abcdefgh'[n] ?? 'x' + n);
}

/**
 * Topologically sort a list of terms, extending it with any missing dependency terms,
 * if necessary. The output list is guaranteed to contain at least the input terms.
 *
 * @param options
 * @param [options.list] - a single expression or a list of expressions to sort.
 * @param [options.env]  - a set of terms assumed to be known (and thus not required in the output list).
 * @param [options.allow]  - a set of terms that are allowed in the returned list (aside from the input list).
 *
 * @example
 *    const expr = ski.parse(src);
 *    toposort([expr], ski.getTerms()); // returns all terms appearing in Expr in correct order
 */
// TODO the docs suck. You know it and I know it. Fix when have time.
export function toposort (options: {list?: Expr|Expr[], env?: Record<string, Named> | undefined, allow?: Record<string, Named> | undefined}): ToposortResult {
  if (typeof options !== 'object' || options === null || Array.isArray(options) || options instanceof Expr)
    throw new Error('positional arguments to toposort are deprecated, use { list: ..., env: ... } instead');

  // TODO enforce both `env` and `allow` to have foo[key].name === key

  const allow = options.allow ? { ...options.allow } : null;
  const env = { ...options.env ?? {} };
  const list = options.list instanceof Expr
    ? [options.list]
    : options.list ?? []; // TODO imply default from allow

  if (allow) {
    // terms from `list` are automatically allowed and trump existing values
    for (const term of list) {
      if (term instanceof Named)
        allow[term.name] = term;
    }
  }

  // env masks terms from being in output, but the terms that were in the list
  //     to begin with should stay in the list.
  for (const term of list) {
    if (term instanceof Named && env[term.name] === term)
      delete env[term.name];
  }

  const out: Expr[] = [];
  // assume all terms in env seen, too
  const seen: Set<Expr> = new Set(Object.values(env));
  const rec = (term: Expr, push: boolean = false) => {
    if (seen.has(term))
      return;
    seen.add(term);
    term.fold(undefined, (_:undefined, e:Expr):TraverseValue<undefined> => {
      if (!(e instanceof Named))
        return;

      // a variable bound by an enclosing lambda is not a real dependency, skip it
      if (e instanceof FreeVar && e.scope instanceof Lambda)
        return;

      // not allowed => fall through
      if (allow && allow[e.name] !== e)
        return;

      // inlined alias => skip, unless explicitly allowed
      if (!allow && e instanceof Alias && e.inline)
        return;

      if (e instanceof Atomic) {
        if (e.selfRef)
          seen.add(e.selfRef);
        rec(e.source);
      }

      // recurse. values in `env` will be auto-skipped because they're "seen"
      if (e !== term) {
        rec(e);
        return control.prune();
      }
    });
    if (push || term instanceof Named)
      out.push(term);
    if (term instanceof Named)
      env[term.name] ||= term;
  };

  for (const term of list)
    rec(term, true);

  return {
    list: out,
    env,
  };
}

export const classes = { Expr, App, Named, FreeVar, Primitive, Atomic, Lambda, Church, Alias };
